import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function InvestorRoadmapPage() {
  return <div className="min-h-screen bg-gray-50 font-sans text-[#111111]" data-id="element-2284">
      {/* Private Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-2285">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-2286">
          <div className="flex items-center space-x-4" data-id="element-2287">
            <Link to="/investor-deck" className="text-gray-500 hover:text-[#631bb7] transition-colors" data-id="element-2288">
              <ArrowLeftIcon size={20} data-id="element-2289" />
            </Link>
            <div className="h-6 w-px bg-gray-200" data-id="element-2290"></div>
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-6" data-id="element-2291" />
            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wide" data-id="element-2292">
              Investor Portal
            </span>
          </div>
          <div className="text-sm text-gray-500" data-id="element-2293">
            Confidential & Proprietary
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-12" data-id="element-2294">
        <div className="space-y-4" data-id="element-2295">
          <h1 className="text-4xl font-bold text-[#111111]" data-id="element-2296">
            Product Roadmap & Strategic Priorities
          </h1>
          <p className="text-xl text-gray-500" data-id="element-2297">
            Building in phases — from education to unified orchestration
            platform.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-200 shadow-sm space-y-10" data-id="element-2298">
          {/* Intro */}
          <section data-id="element-2299">
            <p className="text-gray-600 leading-relaxed text-lg" data-id="element-2300">
              Caveo's product roadmap balances rapid time-to-market with
              strategic defensibility. We build in phases, starting with
              high-impact education and layering in managed services, strategic
              integrations, and eventually a unified orchestration platform.
              Each phase has a clear milestone trigger before we move to the
              next.
            </p>
          </section>

          {/* 1. Our Build Philosophy */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2301">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-2302">
              Our Build Philosophy
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-2303">
              <p data-id="element-2304">
                <span className="font-bold text-[#111111]" data-id="element-2305">
                  Start with teaching.
                </span>{' '}
                Education scales infinitely, builds trust immediately, and
                creates the foundation for everything else. We are not trying to
                replace every security tool on day one. We are building the
                connective tissue that makes existing tools work together, then
                selectively integrating where we can deliver meaningfully better
                outcomes.
              </p>
              <p data-id="element-2306">
                <span className="font-bold text-[#111111]" data-id="element-2307">
                  Platform-agnostic by design.
                </span>{' '}
                We meet families where they are: iOS, Android, Windows, Mac,
                ChromeOS. No ecosystem lock-in, no forced migrations, just
                better security through better understanding.
              </p>
              <p data-id="element-2308">
                <span className="font-bold text-[#111111]" data-id="element-2309">
                  Role-aware from the start.
                </span>{' '}
                Every feature must serve the multi-generational household. What
                makes sense for a Guardian (the adult child coordinating family
                security) is different from what makes sense for an Adult Member
                (an aging parent) or a Child Member (a grandchild learning
                digital literacy). This distinction shapes every decision we
                make.
              </p>
            </div>
          </section>

          {/* 2. Phase 1 */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2310">
            <div className="flex items-center gap-4 mb-6" data-id="element-2311">
              <h2 className="text-2xl font-bold text-[#631bb7]" data-id="element-2312">
                Phase 1: The Web App Foundation
              </h2>
              <span className="bg-[#f0ebf8] text-[#631bb7] text-sm font-bold px-3 py-1 rounded-full" data-id="element-2313">
                Now through Month 6
              </span>
            </div>

            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-2314">
              Phase 1 establishes Caveo's core web experience and begins iOS
              development in parallel. This is an invite-only, waitlist-gated
              early access period. We are refining the product with real
              families while building toward the iOS launch in Phase 2.
            </p>

            <div className="space-y-8" data-id="element-2315">
              <div data-id="element-2316">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2317">
                  Core Value Proposition
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2318">
                  Families get immediate clarity, confidence, and actionable
                  next steps through AI-powered personalized education. Within
                  five minutes of arriving on the platform, a user understands
                  where they stand and exactly what to do next.
                </p>
              </div>

              <div data-id="element-2319">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2320">
                  Assessment and Onboarding
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2321">
                  Caveo uses a two-stage discovery process. The first stage is a
                  3-4 minute quick-win survey that delivers immediate value and
                  begins mapping the household. The second is a 10-minute
                  deep-dive diagnostic that captures the full device inventory,
                  identifies subscription overlap (Wallet Leakage), and maps
                  family topology so we understand who supports whom. The output
                  is a personalized action plan, not a grade.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2322">
                  Goal-first onboarding anchors the experience. Users answer one
                  question first: what brings you here today? Entry paths
                  include:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600" data-id="element-2323">
                  <li data-id="element-2324">
                    I think I have been hacked or scammed (Crisis Mode,
                    immediate triage)
                  </li>
                  <li data-id="element-2325">
                    I want my family to be safer online (Guardian Flow, role
                    selection and family invitation)
                  </li>
                  <li data-id="element-2326">
                    Someone invited me to join their family plan (Join Flow,
                    simplified onboarding)
                  </li>
                  <li data-id="element-2327">
                    I want to learn how to protect myself (Solo Adult Flow,
                    personalized assessment)
                  </li>
                  <li data-id="element-2328">
                    I am helping my parent or grandparent get started (Support
                    Flow, guided setup assistance)
                  </li>
                </ul>
              </div>

              <div data-id="element-2329">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2330">
                  37-Module Curriculum
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2331">
                  Caveo's educational content is structured across three
                  progressive levels plus specialized guides, totaling 10-18
                  hours of guided learning completed at the family's own pace.
                  All content is informed by proven learning science frameworks
                  and designed to be immediately actionable.
                </p>
                <ul className="space-y-3 text-gray-600 mb-4" data-id="element-2332">
                  <li data-id="element-2333">
                    <span className="font-bold text-[#111111]" data-id="element-2334">
                      Level 1: Essential Digital Safety:
                    </span>{' '}
                    9 modules, 2.5-4 hours. The foundation every household
                    member needs.
                  </li>
                  <li data-id="element-2335">
                    <span className="font-bold text-[#111111]" data-id="element-2336">
                      Level 2: Intermediate Protection:
                    </span>{' '}
                    11 modules, 7-12 hours. Building comprehensive household
                    security.
                  </li>
                  <li data-id="element-2337">
                    <span className="font-bold text-[#111111]" data-id="element-2338">
                      Level 3: Advanced Security Practices:
                    </span>{' '}
                    8 modules, 4-9 hours. For households ready to go further.
                  </li>
                  <li data-id="element-2339">
                    <span className="font-bold text-[#111111]" data-id="element-2340">
                      Specialized Guides:
                    </span>{' '}
                    Targeted, situation-specific guidance for scenarios
                    including financial scams, AI safety, identity masking, and
                    more.
                  </li>
                </ul>
                <p className="text-gray-600 leading-relaxed" data-id="element-2341">
                  Every module follows a proven five-part structure: Hook (a
                  relatable story or oops moment), Concept (one key idea per
                  screen with visuals and analogies), Practice (an interactive
                  activity to apply the concept), Reflection (a prompt to
                  connect the lesson to the learner's own life), and Takeaway
                  (one clear action to complete right now). Mastery gates on
                  critical concepts ensure understanding before advancing.
                </p>
              </div>

              <div data-id="element-2342">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2343">
                  Protection Score
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2344">
                  A 1,000-point scoring system measures capability building
                  across the household. 0 to 450 reflects basic safety hygiene
                  in place. 450 to 1,000 reflects a legitimately secured
                  household. The score is visible on both individual and family
                  dashboards, designed to motivate rather than shame, and
                  updated dynamically as modules are completed and actions are
                  taken.
                </p>
              </div>

              <div data-id="element-2345">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2346">
                  Role-Based Dashboard
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2347">
                  Five distinct roles with appropriate permissions and tailored
                  interfaces: Guardian (full family visibility, billing, module
                  assignment, aggregated Protection Score), Super Guardian
                  (elevated technical permissions, remote assistance
                  capabilities launching in Phase 2), Adult Member (self-managed
                  with optional Guardian oversight), Child Member
                  (age-appropriate content with Guardian-approved permissions),
                  and Account Member (individual access without family
                  participation).
                </p>
              </div>

              <div data-id="element-2348">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2349">
                  Living Tech Roadmap and Infrastructure Management
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2350">
                  Caveo tracks each household's device ecosystem with lifecycle
                  awareness. Ecosystem-aware shopping guides warn families
                  against common purchasing mistakes (such as buying
                  incompatible or redundant hardware) and surface
                  recommendations based on what the family already owns. Device
                  health indicators surface OS version status, MFA enablement,
                  router security checklist completion, and backup status.
                </p>
              </div>

              <div data-id="element-2351">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2352">
                  Crisis Response Guide
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2353">
                  Accessible without signup. The Crisis Response Guide is
                  Caveo's primary top-of-funnel tool and serves as the entry
                  point for families in crisis. When someone searches for help
                  after a scam or account compromise, they find Caveo's
                  comprehensive, empathetic triage.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2354">
                  Triage pathways include:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-4" data-id="element-2355">
                  <li data-id="element-2356">
                    Did you send money or gift cards (financial fraud protocol)
                  </li>
                  <li data-id="element-2357">
                    Did you click a link or download something (malware
                    assessment)
                  </li>
                  <li data-id="element-2358">
                    Did you share passwords or account information (account
                    compromise workflow)
                  </li>
                  <li data-id="element-2359">
                    Are you being threatened or blackmailed (escalation to
                    authorities)
                  </li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2360">
                  Every path provides real-time guidance, clear next steps, and
                  referrals to appropriate resources including the FTC, FBI IC3,
                  local law enforcement, and bank fraud departments. There is no
                  sugarcoating and no signup required.
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-2361">
                  The Crisis Response Guide creates the relationship with
                  families at their most vulnerable moment and serves as the
                  soft conversion point into the paid platform. It is the most
                  important acquisition asset Caveo owns.
                </p>
              </div>

              <div data-id="element-2362">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2363">
                  AI Advisor (Launches Month 4)
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2364">
                  Caveo's AI Advisor is a conversational support layer that is
                  contextually aware of the family's setup, progress, and device
                  ecosystem. It handles natural language troubleshooting (for
                  example, my mom cannot log into her email), delivers
                  step-by-step guided resolution with screenshots, and escalates
                  to human support for complex issues. Available 24/7 and
                  accessible from within the platform at any time.
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-2365">
                  The AI Advisor also powers vendor evaluation and shopping
                  assistance. Families receive plain-English comparison guides,
                  honest recommendations that include telling families when they
                  do not need to buy anything, and implementation support for
                  any tool they choose. Caveo earns no affiliate revenue, which
                  is what makes these recommendations credible.
                </p>
              </div>

              <div data-id="element-2366">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2367">
                  iOS Development (Running in Parallel)
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2368">
                  iOS development runs concurrently with the web app throughout
                  Phase 1. Target: TestFlight closed beta in September with
                  100-500 power users drawn from the web app. Full iOS launch is
                  planned for October, coinciding with Cybersecurity Awareness
                  Month, and is the centerpiece of Phase 2.
                </p>
              </div>
            </div>
          </section>

          {/* 3. Phase 2 */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2369">
            <div className="flex items-center gap-4 mb-6" data-id="element-2370">
              <h2 className="text-2xl font-bold text-[#631bb7]" data-id="element-2371">
                Phase 2: iOS Launch, Remote Assist, and Human Concierge
              </h2>
              <span className="bg-[#f0ebf8] text-[#631bb7] text-sm font-bold px-3 py-1 rounded-full" data-id="element-2372">
                Months 7 through 12
              </span>
            </div>

            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-2373">
              Phase 2 transforms Caveo from an education platform into a full
              family technology partner. The iPhone app launches in October
              during Cybersecurity Awareness Month. Remote Assist and the
              Concierge service introduce hands-on support capability. Android
              development begins. By the end of Phase 2, Caveo is competing
              directly with Geek Squad and HelloTech at a fraction of the cost
              with unlimited support.
            </p>

            <div className="space-y-8" data-id="element-2374">
              <div data-id="element-2375">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2376">
                  iOS App Launch (Month 7, October)
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2377">
                  The iPhone app launches publicly in October. This timing is
                  deliberate: Cybersecurity Awareness Month provides a natural
                  earned media hook and a concentrated window of consumer
                  attention. The app delivers feature parity with the web
                  experience, optimized for mobile with biometric login, push
                  notifications, and offline module access.
                </p>
              </div>

              <div data-id="element-2378">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2379">
                  Remote Assist
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2380">
                  Remote Assist launches on desktop in October alongside the iOS
                  app. It enables two distinct use cases: Guardian-to-family
                  support (a family member helping a parent or grandparent
                  remotely) and Caveo staff-to-family support (escalated
                  technical assistance from the Caveo team). Every session
                  requires explicit consent, is logged, and includes a session
                  recording for safety and compliance. The boundary between a
                  Guardian helping and Caveo intervening is always transparent.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2381">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-2382">
                    <p className="text-gray-600" data-id="element-2383">
                      <span className="font-bold text-[#111111]" data-id="element-2384">Pricing:</span>{' '}
                      <span className="font-bold text-[#631bb7]" data-id="element-2385">
                        $9.99 per month
                      </span>{' '}
                      for unlimited Remote Assist access.
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-2386">
                    <p className="text-gray-600" data-id="element-2387">
                      <span className="font-bold text-[#111111]" data-id="element-2388">
                        Competitive context:
                      </span>{' '}
                      Geek Squad and HelloTech charge $99-199 per incident.
                      Caveo delivers ongoing access for a monthly flat fee,
                      representing 10-20x better value for families who need
                      regular support.
                    </p>
                  </div>
                </div>
              </div>

              <div data-id="element-2389">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2390">
                  Wallet Leakage Detection and Subscription Audit
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2391">
                  Building on the initial onboarding assessment, Phase 2
                  introduces a deeper subscription audit layer. Families can
                  securely connect accounts to surface redundant service spend
                  across their household. The platform identifies overlapping
                  subscriptions, flags services the family is paying for but not
                  using, and surfaces consolidation opportunities in plain
                  English. This feature reinforces Caveo's fiduciary
                  positioning: we save families money rather than adding to
                  their stack.
                </p>
              </div>

              <div data-id="element-2392">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2393">
                  Centralized Family Tech Inbox
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2394">
                  All issues, alerts, and support requests across the family
                  land in one shared view. Guardians can assign tasks to
                  themselves, to a specific family member, or to the Caveo
                  Concierge team. Every item has status tracking (Open, In
                  Progress, Waiting, Resolved) and a playbook-driven resolution
                  path. Completed actions generate proof of completion and an
                  audit trail.
                </p>
                <p className="text-gray-600 leading-relaxed" data-id="element-2395">
                  This layer becomes Caveo's most defensible infrastructure. The
                  family's entire digital safety history, every issue resolved,
                  every task completed, every configuration change made, lives
                  inside Caveo. That history creates meaningful switching costs
                  and becomes the foundation for the intelligence layer that
                  develops in Phases 3 and 4.
                </p>
              </div>

              <div data-id="element-2396">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2397">
                  Phishing Campaign Testing
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2398">
                  An optional, consent-based feature for families who want to
                  pressure-test what they have learned. Caveo sends simulated
                  phishing emails and smishing SMS messages to consenting family
                  members. If a family member clicks, they receive immediate
                  feedback and targeted remedial education rather than shame.
                  The experience is gamified: Can you spot the scam? This
                  feature is particularly effective for households with aging
                  adults who are high-value targets for social engineering
                  attacks.
                </p>
              </div>

              <div data-id="element-2399">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2400">
                  Data Broker Removal and Dark Web Monitoring (Partner)
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2401">
                  Caveo integrates with data broker removal APIs and breach
                  monitoring services to surface household exposure in a single
                  dashboard. Plain-English explanations accompany every alert:
                  here is what this means, here is why it matters, and here is
                  exactly what to do next. Action buttons connect directly into
                  the Concierge workflow for families who want hands-on
                  remediation support.
                </p>
              </div>

              <div data-id="element-2402">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2403">
                  Concierge Service
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2404">
                  For complex issues that go beyond what the AI Advisor and
                  Remote Assist can resolve, Caveo offers on-demand white-glove
                  support from dedicated technical experts. Concierge is
                  designed for situations like full device migrations, complex
                  smart home configurations, incident response after a confirmed
                  compromise, or families managing the digital affairs of a
                  cognitively declining parent.
                </p>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 inline-block" data-id="element-2405">
                  <p className="text-gray-600" data-id="element-2406">
                    <span className="font-bold text-[#111111]" data-id="element-2407">Pricing:</span>{' '}
                    <span className="font-bold text-[#631bb7]" data-id="element-2408">
                      $199 per hour
                    </span>
                    . No bundles, no subscriptions. Families pay for exactly
                    what they need.
                  </p>
                </div>
              </div>

              <div data-id="element-2409">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2410">
                  Android Development Begins
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2411">
                  Android development starts in Month 7 and runs through the end
                  of Phase 2. The fragmentation challenge of the Android
                  ecosystem, thousands of device configurations and OS versions
                  requiring extensive QA, is why Android follows iOS rather than
                  launching simultaneously. Feature parity is the target by the
                  Phase 3 launch.
                </p>
              </div>
            </div>
          </section>

          {/* 4. Phase 3 */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2412">
            <div className="flex items-center gap-4 mb-6" data-id="element-2413">
              <h2 className="text-2xl font-bold text-[#631bb7]" data-id="element-2414">
                Phase 3: Android Launch and Strategic Integrations
              </h2>
              <span className="bg-[#f0ebf8] text-[#631bb7] text-sm font-bold px-3 py-1 rounded-full" data-id="element-2415">
                Months 12 through 18
              </span>
            </div>

            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-2416">
              Phase 3 delivers the Android app and expands the platform through
              a combination of Caveo-native features and strategic third-party
              integrations. The build-versus-partner decision framework is
              applied rigorously: Caveo builds where we can deliver a
              meaningfully better family-aware experience, and partners where an
              excellent solution already exists and integration is sufficient.
            </p>

            <div className="space-y-8" data-id="element-2417">
              <div data-id="element-2418">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2419">
                  Android App Launch (Month 12)
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2420">
                  The Android app launches with full feature parity to iOS. With
                  both platforms live, Caveo covers more than 95% of the
                  smartphone market. The Android launch removes the last major
                  accessibility barrier for families with mixed-device
                  households.
                </p>
              </div>

              <div data-id="element-2421">
                <h3 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2422">
                  Caveo-Native Features (We Build)
                </h3>
                <div className="space-y-6" data-id="element-2423">
                  <div data-id="element-2424">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2425">
                      Email Alias Generation
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2426">
                      A lightweight, hide-my-email style service that generates
                      unique email aliases for signups, online shopping, and
                      account creation. This prevents spam, reduces data breach
                      exposure, and makes it easier to identify and cut off
                      compromised addresses. Delivered in plain English with no
                      technical setup required.
                    </p>
                  </div>
                  <div data-id="element-2427">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2428">
                      Family SSO and Identity Access Management
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2429">
                      A unified authentication portal for the household, modeled
                      on how Okta became indispensable to enterprises. Once
                      families authenticate through Caveo SSO, Caveo becomes the
                      gateway layer for their digital life. This is one of the
                      most important moat-building features on the roadmap: once
                      a family is authenticating through Caveo, switching costs
                      become very high. This feature mirrors enterprise IAM
                      practices applied to the household context.
                    </p>
                  </div>
                  <div data-id="element-2430">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2431">
                      Family Verification Protocol and Deepfake Defense
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2432">
                      With AI-powered voice cloning and deepfake technology
                      making impersonation attacks more sophisticated every
                      year, families need a verified way to confirm they are
                      actually talking to each other. Caveo builds safe word
                      creation flows, deepfake scam rehearsal scenarios, and
                      emergency response checklists into the platform. Families
                      practice recognizing and responding to impersonation
                      attempts before they happen.
                    </p>
                  </div>
                </div>
              </div>

              <div data-id="element-2433">
                <h3 className="text-xl font-bold text-[#111111] mb-4" data-id="element-2434">
                  Strategic Integrations (We Partner)
                </h3>
                <div className="space-y-6" data-id="element-2435">
                  <div data-id="element-2436">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2437">
                      Password Manager Integration
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2438">
                      OAuth integration with 1Password, iCloud Keychain, and
                      Google Password Manager provides a unified view of
                      password health across the household. Caveo does not build
                      a competing password manager. Instead, we teach families
                      how to choose the right one for their situation, guide
                      them through setup and browser integration, and surface
                      weak or reused passwords in the dashboard. This is the
                      adoption gap that existing password manager vendors have
                      failed to close, and it is one of Caveo's clearest
                      value-adds.
                    </p>
                  </div>
                  <div data-id="element-2439">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2440">
                      Financial Wellbeing Monitoring
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2441">
                      API connections with providers like Aura, IdentityGuard,
                      and Credit Karma deliver a consolidated view of credit
                      monitoring, dark web scans, and financial alerts within
                      the Caveo dashboard. Families get one place to see their
                      exposure without managing multiple separate logins and
                      interfaces.
                    </p>
                  </div>
                  <div data-id="element-2442">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2443">
                      Backup Service Integration
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2444">
                      Read-only coordination with iCloud, Google Drive,
                      Backblaze, and Carbonite verifies that backups are
                      enabled, recent, and actually restorable. Caveo surfaces
                      family-wide backup status and guides restoration testing.
                      The most common backup failure mode is discovering the
                      backup did not work when you actually need it. Caveo
                      addresses this proactively.
                    </p>
                  </div>
                  <div data-id="element-2445">
                    <h4 className="font-bold text-[#111111] mb-2" data-id="element-2446">
                      Social Impersonation Monitoring
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-2447">
                      Partner integration for monitoring unauthorized use of
                      family members' identities on social platforms. This is
                      particularly relevant for households with aging adults who
                      are high-value targets for account impersonation and
                      romance scams.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 5. Phase 4 */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2448">
            <div className="flex items-center gap-4 mb-6" data-id="element-2449">
              <h2 className="text-2xl font-bold text-[#631bb7]" data-id="element-2450">
                Phase 4: Unified Platform and Intelligence Layer
              </h2>
              <span className="bg-[#f0ebf8] text-[#631bb7] text-sm font-bold px-3 py-1 rounded-full" data-id="element-2451">
                Months 18 through 36
              </span>
            </div>

            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-2452">
              Phase 4 deepens the intelligence and orchestration layer rather
              than building competing point solutions. By this stage, Caveo has
              accumulated meaningful behavioral data, family graph intelligence,
              and a track record of household security outcomes. That foundation
              enables a fundamentally different kind of product: one that
              anticipates problems rather than reacting to them.
            </p>

            <div className="space-y-8" data-id="element-2453">
              <div data-id="element-2454">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2455">
                  Predictive Threat Intelligence
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2456">
                  Personalized threat intelligence based on each family's
                  profile, device ecosystem, behavior patterns, and regional
                  threat landscape. Rather than generic alerts, families receive
                  specific, contextual warnings: this type of scam is actively
                  targeting households with your profile in your area, and here
                  is what to watch for. The platform moves from reactive to
                  genuinely predictive.
                </p>
              </div>

              <div data-id="element-2457">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2458">
                  AI-Powered Scam Detection
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2459">
                  Proactive monitoring that surfaces scam attempts with
                  plain-English explanations of what is happening and why.
                  Rather than a technical alert that a suspicious email was
                  detected, families receive a clear explanation of the social
                  engineering technique being used, why it targets people like
                  them, and exactly what to do. Education and protection are
                  delivered in the same moment.
                </p>
              </div>

              <div data-id="element-2460">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2461">
                  Predictive Maintenance
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2462">
                  Anticipating hardware failures, alerting to device end-of-life
                  before it becomes a crisis, flagging software that is
                  approaching unsupported status, and identifying configurations
                  that are likely to cause problems before they do. The
                  Centralized Family Tech Inbox built in Phase 2 becomes the
                  operational backbone for this layer, with proactive tasks
                  surfacing automatically rather than waiting for a family
                  member to notice a problem.
                </p>
              </div>

              <div data-id="element-2463">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2464">
                  Insurance Partnership
                </h3>
                <p className="text-gray-600 leading-relaxed mb-2" data-id="element-2465">
                  A cyber insurance integration that plugs directly into Caveo's
                  orchestration layer. This is introduced only after Caveo has
                  accumulated meaningful data on claim frequency and household
                  risk profiles, and has negotiated terms that reflect Caveo's
                  education-first, lower-risk customer base. When an incident
                  occurs, the claim process becomes a guided workflow inside the
                  platform rather than a PDF download and a phone call. Families
                  who have completed the curriculum and maintained high
                  Protection Scores should qualify for favorable terms that
                  reflect their lower actual risk.
                </p>
                <p className="text-gray-500 italic text-sm" data-id="element-2466">
                  *Insurance is added last, after data and claim frequency are
                  well understood. This sequencing is intentional.*
                </p>
              </div>

              <div data-id="element-2467">
                <h3 className="text-xl font-bold text-[#111111] mb-3" data-id="element-2468">
                  B2B2C Institutional Partnerships (Maturing)
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2469">
                  By Phase 4, the B2B2C channel is generating meaningful revenue
                  and building toward category leadership. Institutional
                  partnerships with senior living communities, financial
                  institutions, and Medicare Advantage plans are fully
                  operational, with white-label experiences, admin dashboards,
                  and revenue share structures in place. These partnerships
                  expand Caveo's reach into the hardest-to-acquire demographics
                  at dramatically lower CAC than direct consumer channels.
                </p>
              </div>
            </div>
          </section>

          {/* 6. Strategic Priorities Across All Phases */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2470">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-2471">
              Strategic Priorities Across All Phases
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-2472">
              These principles apply to every feature decision across the entire
              roadmap, regardless of phase.
            </p>

            <div className="space-y-6" data-id="element-2473">
              <div data-id="element-2474">
                <h3 className="text-lg font-bold text-[#111111] mb-2" data-id="element-2475">
                  Education Always Comes First
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2476">
                  Even as Caveo adds managed services and integrations,
                  education remains the core. Every feature must answer one
                  question: how does this help families learn and improve?
                  Features that deliver capability without understanding create
                  dependency. Features that build understanding first create
                  loyalty.
                </p>
              </div>
              <div data-id="element-2477">
                <h3 className="text-lg font-bold text-[#111111] mb-2" data-id="element-2478">
                  Platform-Agnostic, Forever
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2479">
                  Caveo will never lock families into a device ecosystem. iOS
                  and Android. Mac and Windows. ChromeOS. Mixed households. We
                  support all of them. This is both a product principle and a
                  competitive positioning decision: Apple and Google cannot
                  serve mixed-device households without conflicts of interest.
                  Caveo can, and we always will.
                </p>
              </div>
              <div data-id="element-2480">
                <h3 className="text-lg font-bold text-[#111111] mb-2" data-id="element-2481">
                  Role-Aware, Always
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2482">
                  Every feature must serve the multi-generational household.
                  Guardians, Adult Members, Child Members, and Account Members
                  have fundamentally different relationships to technology,
                  different risk profiles, and different support needs. A
                  feature that works well for a 45-year-old Guardian may be
                  intimidating or irrelevant for a 72-year-old Adult Member.
                  Role-aware design is not a nice-to-have; it is the core of
                  what makes Caveo different.
                </p>
              </div>
              <div data-id="element-2483">
                <h3 className="text-lg font-bold text-[#111111] mb-2" data-id="element-2484">
                  Privacy by Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2485">
                  Caveo will never sell user data. Caveo will never use
                  surveillance as a business model. We earn money by genuinely
                  helping families, not by monetizing their behavior. Zero
                  affiliate revenue preserves the integrity of every
                  recommendation we make. This is not a marketing position; it
                  is a structural commitment built into how the company
                  operates.
                </p>
              </div>
              <div data-id="element-2486">
                <h3 className="text-lg font-bold text-[#111111] mb-2" data-id="element-2487">
                  Build Versus Partner Decision Framework
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2488">
                  Caveo partners when an existing solution is excellent and
                  approachable, when integration is sufficient for user needs,
                  and when building would produce an undifferentiated outcome.
                  Caveo builds when existing solutions are too complex or
                  technical for mainstream families, when integration
                  limitations create a poor user experience, when we can deliver
                  a meaningfully better family-aware outcome, and when revenue
                  capture justifies the development investment. This framework
                  keeps the roadmap disciplined and prevents scope creep into
                  areas where Caveo would not win.
                </p>
              </div>
            </div>
          </section>

          {/* 7. Competitive Moats We Are Building */}
          <section className="border-t border-gray-100 pt-10" data-id="element-2489">
            <h2 className="text-2xl font-bold mb-6 text-[#631bb7]" data-id="element-2490">
              Competitive Moats We Are Building
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-2491">
              The roadmap is designed to compound over time. Each phase adds a
              layer of defensibility that makes Caveo harder to displace.
            </p>
            <ul className="space-y-4 text-gray-600" data-id="element-2492">
              <li className="flex items-start" data-id="element-2493">
                <span className="mr-3 mt-1 text-[#631bb7]" data-id="element-2494">•</span>
                <span data-id="element-2495">
                  <span className="font-bold text-[#111111]" data-id="element-2496">
                    Proprietary Educational Content:
                  </span>{' '}
                  37 modules, continuously updated, backed by learning science.
                  A competitor would need 12-18 months and $500K-1M to replicate
                  the content alone, and still would not have the family graph
                  data or usage patterns that make Caveo's personalization
                  effective.
                </span>
              </li>
              <li className="flex items-start" data-id="element-2497">
                <span className="mr-3 mt-1 text-[#631bb7]" data-id="element-2498">•</span>
                <span data-id="element-2499">
                  <span className="font-bold text-[#111111]" data-id="element-2500">
                    Family Graph and Behavioral Data:
                  </span>{' '}
                  Understanding family dynamics, device ecosystems, literacy
                  levels, and household risk profiles. The AI personalization
                  improves with usage, and the data compounds: more families
                  means better recommendations for every family.
                </span>
              </li>
              <li className="flex items-start" data-id="element-2501">
                <span className="mr-3 mt-1 text-[#631bb7]" data-id="element-2502">•</span>
                <span data-id="element-2503">
                  <span className="font-bold text-[#111111]" data-id="element-2504">
                    Multi-Generational UX Expertise:
                  </span>{' '}
                  Designing for Boomers and Gen Z simultaneously within the same
                  product is extremely difficult to execute well. It requires
                  deep user research, extensive testing, and genuine empathy for
                  users across a wide range of technical literacy. This is not
                  something a competitor can acquire quickly.
                </span>
              </li>
              <li className="flex items-start" data-id="element-2505">
                <span className="mr-3 mt-1 text-[#631bb7]" data-id="element-2506">•</span>
                <span data-id="element-2507">
                  <span className="font-bold text-[#111111]" data-id="element-2508">
                    Platform-Agnostic Positioning:
                  </span>{' '}
                  The only player serving mixed-device households without
                  ecosystem conflicts. Apple and Google cannot do this by
                  design. Legacy vendors lack the organizational will. Caveo
                  occupies this position by strategic choice.
                </span>
              </li>
              <li className="flex items-start" data-id="element-2509">
                <span className="mr-3 mt-1 text-[#631bb7]" data-id="element-2510">•</span>
                <span data-id="element-2511">
                  <span className="font-bold text-[#111111]" data-id="element-2512">
                    Trust in Crisis Moments:
                  </span>{' '}
                  The Crisis Response Guide builds the relationship when
                  families are most vulnerable. That trust is extraordinarily
                  difficult to replicate and is the foundation of every
                  conversion and retention outcome that follows.
                </span>
              </li>
              <li className="flex items-start" data-id="element-2513">
                <span className="mr-3 mt-1 text-[#631bb7]" data-id="element-2514">•</span>
                <span data-id="element-2515">
                  <span className="font-bold text-[#111111]" data-id="element-2516">
                    Education Plus Managed Services Hybrid:
                  </span>{' '}
                  Scalable education combined with high-touch human support.
                  Competitors are either pure product or pure service. Caveo's
                  combination is unique and creates a moat that neither side of
                  the market can easily replicate.
                </span>
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-12" data-id="element-2517">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-500" data-id="element-2518">
          <p data-id="element-2519">&copy; 2026 AppEmbrace, Inc. DBA Caveo. All rights reserved.</p>
          <p className="mt-2 text-xs text-gray-400" data-id="element-2520">
            This page contains confidential information intended only for
            authorized investors. Do not share or distribute without permission.
          </p>
        </div>
      </footer>
    </div>;
}